import React, { useState, useEffect } from 'react';
import { useHealthStore } from './stores/useHealthStore';
import { useAuth } from '../../hooks/useAuth';
import { Heart, ArrowLeft, History, Scale, Activity, Zap } from 'lucide-react';
import '../shared-premium.css';

interface HealthTrackerAppProps {
    onExit: () => void;
}

/**
 * HealthTrackerApp: Strategic Biometrics & Weight Ledger.
 * HARDENED: Strict viewport-constrained layout to match AgendaView sizing.
 */
const HealthTrackerApp: React.FC<HealthTrackerAppProps> = ({ onExit }) => {
    const { session } = useAuth();
    const { logs, addLog, fetchLogs, isSyncing } = useHealthStore();
    
    // Inputs
    const [weight, setWeight] = useState<string>('');
    const [sys, setSys] = useState<string>('120');
    const [dia, setDia] = useState<string>('80');
    const [hr, setHr] = useState<string>('70');

    useEffect(() => {
        if (session?.user?.id) fetchLogs(session.user.id);
    }, [session, fetchLogs]);

    const handleSaveWeight = async () => {
        if (!session?.user?.id || !weight) return;
        await addLog(session.user.id, { weight: parseFloat(weight) });
        setWeight('');
    };

    const handleSaveVitals = async () => {
        if (!session?.user?.id) return;
        await addLog(session.user.id, {
            bp_sys: sys ? parseInt(sys) : undefined,
            bp_dia: dia ? parseInt(dia) : undefined,
            hr: hr ? parseInt(hr) : undefined
        });
    };

    return (
        <div className="app-container-v2 viewport-constrained" style={{ backgroundColor: '#000', height: '100dvh' }}>
            <div className="scrollable-content" style={{ padding: '20px' }}>
                
                {/* Header */}
                <header style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                    <button onClick={onExit} style={{ all: 'unset', cursor: 'pointer', padding: '10px' }}>
                        <ArrowLeft size={24} color="#f29b11" />
                    </button>
                    <h1 style={{ flex: 1, textAlign: 'center', fontSize: '18px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '2px', marginRight: '44px', color: '#fff' }}>
                        Health <span style={{ color: '#f29b11' }}>Ledger</span>
                    </h1>
                </header>

                {/* 1. WEIGHT MODULE */}
                <div className="premium-card" style={{ marginBottom: '20px', borderLeft: '5px solid #3b82f6' }}>
                    <div className="section-header" style={{ color: '#3b82f6' }}>
                        <Scale size={14} style={{ marginRight: '8px' }} /> BODY MASS INDEX
                    </div>
                    <div style={{ marginTop: '15px' }}>
                        <div className="premium-input-group">
                            <label style={{ fontSize: '9px', fontWeight: '900', color: '#444', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'block' }}>CURRENT WEIGHT (KG)</label>
                            <input 
                                type="number" 
                                inputMode="decimal"
                                className="premium-input" 
                                placeholder="0.0"
                                value={weight} 
                                onChange={(e) => setWeight(e.target.value)} 
                                style={{ fontSize: '24px', textAlign: 'center', height: '60px', color: '#3b82f6' }}
                            />
                        </div>
                        <button 
                            className="premium-btn-primary" 
                            onClick={handleSaveWeight}
                            disabled={isSyncing || !weight}
                            style={{ marginTop: '15px', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid #3b82f6' }}
                        >
                            {isSyncing ? 'SYNCING...' : 'COMMIT WEIGHT'}
                        </button>
                    </div>
                </div>

                {/* 2. VITALS MODULE */}
                <div className="premium-card" style={{ marginBottom: '20px', borderLeft: '5px solid #ef4444' }}>
                    <div className="section-header" style={{ color: '#ef4444' }}>
                        <Heart size={14} style={{ marginRight: '8px' }} /> CIRCULATORY VITALS
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginTop: '20px' }}>
                        <div>
                            <label style={{ fontSize: '9px', fontWeight: '900', color: '#444', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>SYS</label>
                            <input type="number" className="premium-input" value={sys} onChange={(e) => setSys(e.target.value)} style={{ textAlign: 'center' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: '9px', fontWeight: '900', color: '#444', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>DIA</label>
                            <input type="number" className="premium-input" value={dia} onChange={(e) => setDia(e.target.value)} style={{ textAlign: 'center' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: '9px', fontWeight: '900', color: '#444', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>PULSE</label>
                            <input type="number" className="premium-input" value={hr} onChange={(e) => setHr(e.target.value)} style={{ textAlign: 'center' }} />
                        </div>
                    </div>
                    <button 
                        className="premium-btn-primary" 
                        onClick={handleSaveVitals}
                        disabled={isSyncing}
                        style={{ marginTop: '15px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid #ef4444' }}
                    >
                        {isSyncing ? 'SYNCING...' : 'COMMIT VITALS'}
                    </button>
                </div>

                {/* 3. TRENDS */}
                <div className="premium-card">
                    <div className="section-header">
                        <History size={14} style={{ marginRight: '8px' }} /> ANALYTICAL TRENDS
                    </div>
                    
                    <div style={{ marginTop: '15px' }}>
                        {logs.length === 0 ? (
                            <p style={{ textAlign: 'center', color: '#444', fontSize: '11px', fontWeight: '900', letterSpacing: '1px', padding: '20px 0' }}>NO DATA RECORDED</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {logs.slice(0, 10).map((log, idx) => (
                                    <div key={idx} style={{ padding: '12px', borderRadius: '8px', border: '1px solid #222', display: 'flex', alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                            <p style={{ fontSize: '10px', color: '#666', fontWeight: '900', textTransform: 'uppercase', marginBottom: '4px' }}>
                                                {new Date(log.measured_at).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                                            </p>
                                            <div style={{ display: 'flex', gap: '15px' }}>
                                                {log.metrics.weight && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <Scale size={10} color="#3b82f6" />
                                                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{log.metrics.weight}</span>
                                                        <span style={{ fontSize: '9px', color: '#3b82f6', fontWeight: '900' }}>KG</span>
                                                    </div>
                                                )}
                                                {(log.metrics.bp_sys || log.metrics.bp_dia) && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <Activity size={10} color="#ef4444" />
                                                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>
                                                            {log.metrics.bp_sys || '?'}/{log.metrics.bp_dia || '?'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {log.metrics.hr && (
                                            <div style={{ textAlign: 'right' }}>
                                                <p style={{ fontSize: '14px', fontWeight: 'black', color: '#f29b11', margin: 0 }}>
                                                    {log.metrics.hr}
                                                </p>
                                                <p style={{ fontSize: '8px', color: '#666', fontWeight: '900' }}>BPM</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <button className="premium-btn-secondary" onClick={onExit} style={{ marginTop: '20px', border: 'none', color: '#444' }}>
                    EXIT TO HUB
                </button>
            </div>
        </div>
    );
};

export default HealthTrackerApp;
